<template>
    <div class="quiz-container">
      <h2 class="quiz-title">{{ quiz.title }}</h2>
      <p class="question-counter">Question {{ currentQuestionIndex + 1 }} of {{ quiz.questions.length }}</p>
      <div class="question-box">
        <h3 class="question">{{ quiz.questions[currentQuestionIndex].question }}</h3>
        <ul class="options-list">
          <li
            v-for="(option, index) in quiz.questions[currentQuestionIndex].options"
            :key="index"
            @click="selectOption(index)"
            :class="{ 'selected': selectedOptionIndex === index, 'correct': isAnswerRevealed && index === quiz.questions[currentQuestionIndex].correctAnswer, 'incorrect': isAnswerRevealed && selectedOptionIndex === index && index !== quiz.questions[currentQuestionIndex].correctAnswer }"
            class="option"
          >
            {{ option }}
            <span v-if="showFeedback && currentQuestion.correctAnswer === index" class="feedback-icon correct">✓</span>
        <span v-if="showFeedback && selectedOption === index && currentQuestion.correctAnswer !== index" class="feedback-icon incorrect">✗</span>
          </li>
        </ul>
      </div>
      <div v-if="isAnswerRevealed" class="feedback">
        <p :class="{ 'feedback-correct': isCorrect, 'feedback-incorrect': !isCorrect }">
          {{ feedbackMessage }}
        </p>
        
      </div>
      <div class="navigation-buttons">
        <button @click="previousQuestion" :disabled="currentQuestionIndex === 0" class="action-button">Back</button>
        <button @click="nextQuestion" :disabled="currentQuestionIndex === quiz.questions.length - 1 || !isAnswerRevealed" class="action-button">Next</button>
      </div>
      <div v-if="currentQuestionIndex === quiz.questions.length - 1 && isAnswerRevealed" class="results-summary">
        <h4>Quiz Summary</h4>
        <p>Correct answers: {{ correctAnswersCount }}</p>
        <p>Incorrect answers: {{ incorrectAnswersCount }}</p>
        <button @click="retakeQuiz" class="action-button">Retake Quiz</button>
        <button @click="goToNextQuiz" class="action-button next-quiz-button">Next Quiz</button>
      </div>
    </div><br><br><br><br><br><br>
  </template>
  
  <script>
import { ref } from 'vue';

export default {
  setup() {
  
    const correctAnswerSound = new Audio('https://assets.codepen.io/11151897/stock-market-bell.mp3');
    const quiz = ref({
      title: "Introduction to Stocks",
      questions: [
        {
          question: "What is a stock?",
          options: [
            "A financial instrument that represents a loan to a company",
            "Ownership in a corporation and represents a claim on part of the corporation's assets and earnings",
            "A fixed income investment that pays interest",
            "A type of cryptocurrency"
          ],
          correctAnswer: 1
        },
        {
          question: "What does it mean to own a share of stock?",
          options: [
            "You have lent money to the company",
            "You own a portion of the company",
            "You are owed a fixed amount of money by the company",
            "You have the right to vote on company matters"
          ],
          correctAnswer: 1
        },
        {
          question: "What is a dividend?",
          options: [
            "A type of stock",
            "A fee charged for buying a stock",
            "A portion of a company's earnings distributed to shareholders",
            "Interest paid on a stock"
          ],
          correctAnswer: 2
        },
        {
          question: "What is the primary role of the stock market?",
          options: [
            "To facilitate the exchange of commodities",
            "To provide loans to businesses and individuals",
            "To allow buyers and sellers to trade stocks",
            "To regulate the national economy"
          ],
          correctAnswer: 2
        },
        {
          question: "What does it mean when a stock is described as 'volatile'?",
          options: [
            "It pays high dividends",
            "Its price changes frequently and unpredictably",
            "It is likely to lose value",
            "It is a safe investment"
          ],
          correctAnswer: 1
        },
        {
          question: "What is a stock index?",
          options: [
            "A list of all stocks on a stock exchange",
            "A measure of the performance of a group of stocks, representing a segment of the market",
            "A rule that stock traders must follow",
            "A database of stock transactions"
          ],
          correctAnswer: 1
        },
        {
          question: "What does it mean to 'short' a stock?",
          options: [
            "To sell a stock you do not own, hoping to buy it back at a lower price",
            "To hold onto a stock for a short period",
            "To sell a stock just before it decreases in value",
            "To buy a stock at a low price"
          ],
          correctAnswer: 0
        },
        {
          question: "What is market capitalization?",
          options: [
            "The total value of all stocks on the market",
            "The amount of capital a company has in the bank",
            "The maximum amount a company can raise through stock sales",
            "The total value of a company's outstanding shares of stock"
          ],
          correctAnswer: 3
        },
        {
          question: "What is a bear market?",
          options: [
            "A market in which stock prices are falling, encouraging selling",
            "A market in which stock prices are rising, encouraging buying",
            "A market that is stable, with little movement in stock prices",
            "A market exclusively for trading commodities"
          ],
          correctAnswer: 0
        },
        {
          question: "What is a bull market?",
          options: [
            "A market in which stock prices are falling, encouraging selling",
            "A market in which stock prices are rising, encouraging buying",
            "A market that is stable, with little movement in stock prices",
            "A market exclusively for trading in agricultural products"
          ],
          correctAnswer: 1
        }
      ]
    });
    const currentQuestionIndex = ref(0);
    const correctAnswersCount = ref(0);
    const incorrectAnswersCount = ref(0);
    const selectedOptionIndex = ref(-1);
    const isAnswerRevealed = ref(false);
    const isCorrect = ref(false);
    const feedbackMessage = ref("");

    const selectOption = (index) => {
      if (isAnswerRevealed.value) return;
      selectedOptionIndex.value = index;
      isAnswerRevealed.value = true;

      if (index === quiz.value.questions[currentQuestionIndex.value].correctAnswer) {
        isCorrect.value = true;
        correctAnswersCount.value++;
        feedbackMessage.value = "Correct!";
        correctAnswerSound.play().catch(error => console.error("Audio play failed", error));
      } else {
        isCorrect.value = false;
        incorrectAnswersCount.value++;
        feedbackMessage.value = "Incorrect.";
      }
    };

    const nextQuestion = () => {
      if (currentQuestionIndex.value < quiz.value.questions.length - 1) {
        currentQuestionIndex.value++;
        resetState();
      }
    };

    const previousQuestion = () => {
      if (currentQuestionIndex.value > 0) {
        currentQuestionIndex.value--;
        resetState();
      }
    };

    const retakeQuiz = () => {
      currentQuestionIndex.value = 0;
      correctAnswersCount.value = 0;
      incorrectAnswersCount.value = 0;
      resetState();
    };

    const resetState = () => {
      selectedOptionIndex.value = -1;
      isAnswerRevealed.value = false;
      isCorrect.value = false;
      feedbackMessage.value = "";
    };

    return {
      quiz,
      currentQuestionIndex,
      correctAnswersCount,
      incorrectAnswersCount,
      selectedOptionIndex,
      isAnswerRevealed,
      isCorrect,
      feedbackMessage,
      selectOption,
      nextQuestion,
      previousQuestion,
      retakeQuiz,
    };
  },
};
</script>


<style scoped>
.quiz-container {
  color: #FFF;
  padding: 20px;
  border-radius: 10px;
  max-width: 600px;
  margin: auto;
  background: linear-gradient(135deg, #6D5BBA 0%, #8D58BF 100%);
}


.feedback-correct, .feedback-incorrect {
  padding: 5px 10px; /* Adds some padding around the text */
  border-radius: 15px; /* Rounds the corners for a bubble-like appearance */
  display: inline-block; /* Makes the background wrap the text */
  color: #FFF; /* Keeps text color white for high contrast */
  font-weight: bold; /* Ensure the text is bold */
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.7); /* Optional: Adds a slight shadow for depth */
}

.feedback-correct {
  background-color: #4CAF50; /* Green background */
  border: 2px solid #FFF; /* White border for contrast */
  color: fff;
}

.feedback-incorrect {
  background-color: #F44336; /* Red background */
  border: 2px solid #FFF; /* White border for contrast */
}
.quiz-title, .question-counter, .question, .feedback {
  text-align: center;
  font-size: 1rem;
}

.results-summary {
  padding: 20px; /* Adds padding inside the summary box */
  background-color: rgba(255, 255, 255, 0.9); /* Semi-transparent white background */
  border-radius: 15px; /* Rounded corners */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Soft shadow for depth */
  margin-top: 20px; /* Space above the summary */
  border: 2px solid #DDD; /* Light border for definition */
}

.results-summary h4 {
  color: #333; /* Darker text for contrast against light background */
  font-size: 1.5rem; /* Larger font size for the title */
}

.results-summary p {
  color: #555; /* Subtle text color for readability */
  font-size: 1rem; /* Larger font size for the content */
}

.options-list {
  list-style-type: none;
  padding: 0;
}

.option {
  margin: 10px 0;
  padding: 10px;
  color: black;
  background-color: #f4f4f8;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 1rem;
}

.option:hover {
  background-color: #d0ccd3;
}

.option.selected.correct {
  border-color: green;
}

.option.selected.incorrect {
  border-color: red;
}
.button-container {
  display: flex;
  justify-content: center; /* Center the buttons horizontally */
  margin-top: 20px; /* Space above the button container */
}
.action-button {
  background-color: #4a148C; 
  color: #DDD;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 25px;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;
  background-color: #36454f; /* Black background */
  color: white;
  justify-content: center; 
}


.action-button:hover {
  background-color: black; /* Dark purple background on hover */
}


.next-quiz-button {
  background-color: #4CAF50; /* Example green background */
  color: white;
  /* Add other styles as needed */
}

.centered-title {
  text-align: center;
  font-size: 2rem; /* Increase font size */
  color: #36454f; /* Dark text for contrast */
  margin-bottom: 20px; /* Add some space below the title */
}

.quiz-container h1 {
  font-size: 1.5rem; /* Make questions stand out */
  color: #4A148C; /* Use the dark purple for questions */
  margin-bottom: 15px;
}

.option-card {
  background-color: #f4f4f8;
  border: 1px solid #ccc;
  padding: 10px;
  margin: 5px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color .3s, transform .2s;
}

.option-card:hover {
  background-color: #e9e9ef;
  transform: scale(1.02); /* Slightly enlarge options on hover */
}

.feedback-icon {
  font-weight: bold;
  margin-left: 15px;
}

.correct {
  color: green;
}

.incorrect {
  color: red;
}

.feedback {
  margin-top: 20px;
  text-align: center;
}

</style>